import {
  Error as ErrorIcon,
  Information as InformationIcon,
  Success as SuccessIcon,
  Warning as WarningIcon,
} from '@mos-cat/ds-icons';
import { HTMLAttributes } from 'react';
import { ResponsiveValue, variant } from 'styled-system';
import { createComponent, createHook } from 'reakit-system';
import { CSSInterpolation } from '@emotion/css';
import { As, forwardRefWithAs } from '../as';
import { Box, useBox } from '../Box/Box';
import { cx, ecss } from '../reexports/emotion';
import { StylesOptions } from '../hooks/useStyles';

import { useRequiredPropWarning } from '../hooks/useRequiredPropWarning';
import { WHCM } from '../constants';

export type MessageOptions = {
  kind: ResponsiveValue<'error' | 'success' | 'warning' | 'info'>;
} & StylesOptions;

/**
 * @beta
 */
export type MessageProps = MessageOptions & HTMLAttributes<HTMLElement>;

const variants = variant({
  prop: 'kind',
  variants: {
    error: {
      borderColor: 'messageErrorBorderColor',
      borderLeftColor: 'messageErrorBorderLeftColor',
      backgroundColor: 'messageErrorBg',
      [`& > svg`]: {
        borderRadius: 'round',
      },
    },
    warning: {
      borderColor: 'messageWarningBorderColor',
      borderLeftColor: 'messageWarningBorderLeftColor',
      backgroundColor: 'messageWarningBg',
      '& > svg': {
        borderRadius: 'round',
      },
    },
    success: {
      borderColor: 'messageSuccessBorderColor',
      borderLeftColor: 'messageSuccessBorderLeftColor',
      backgroundColor: 'messageSuccessBg',
      '& > svg': {
        borderRadius: 'round',
      },
    },
    info: {
      borderColor: 'messageInfoBorderColor',
      borderLeftColor: 'messageInfoBorderLeftColor',
      backgroundColor: 'messageInfoBg',
      '& > svg': {
        borderRadius: 'round',
      },
    },
  },
});

const useMessage = createHook<MessageOptions, HTMLAttributes<HTMLElement>>({
  name: 'Message',
  keys: ['kind'],
  compose: [useBox],
  useOptions(options) {
    return {
      ...options,
      styles: {
        position: 'relative',
        maxWidth: 'semi-wide',
        borderWidth: 'messageBorderWidth',
        borderLeftWidth: 'messageBorderLeftWidth',
        borderRadius: 'messageBorderRadius',
        display: 'flex',
        flexDirection: ['column', 'row'],
        alignContent: 'flex-start',
        pt: ['messagePaddingTopSmall', 'messagePaddingTop'],
        pr: ['messagePaddingRightSmall', 'messagePaddingRight'],
        pb: ['messagePaddingBottomSmall', 'messagePaddingBottom'],
        pl: ['messagePaddingLeftSmall', 'messagePaddingLeft'],
        '> svg': {
          borderRadius: '50%',
        },
        [WHCM]: {
          backgroundColor: 'InfoBackground',
        },
        ...options.styles,
      },
    };
  },
  useProps(options, props) {
    return {
      ...props,
      className: cx(
        props.className,
        ecss(
          {
            label: 'Message',
          },
          variants(options) as CSSInterpolation
        )
      ),
    };
  },
});

const MessageWrapper = createComponent({
  as: 'div',
  useHook: useMessage,
  memo: true,
});

function MessageComponent(
  { children, kind, ...props }: MessageProps,
  ref: React.Ref<HTMLDivElement>
) {
  useRequiredPropWarning(Boolean(kind), 'Message', 'kind');
  let Icon;
  if (kind === 'error') Icon = ErrorIcon;
  if (kind === 'warning') Icon = WarningIcon;
  if (kind === 'success') Icon = SuccessIcon;
  if (kind === 'info') Icon = InformationIcon;
  return (
    <MessageWrapper kind={kind} {...props} ref={ref}>
      <Box
        as={Icon as As}
        flexShrink={0}
        mb={['messageIconMarginBottom', 'sp-0']}
        mr={['sp-0', 'messageIconMarginRight']}
        size="messageIcon"
      />
      <Box>{children}</Box>
    </MessageWrapper>
  );
}

/**
 * @beta
 */
export const Message = forwardRefWithAs<MessageProps, 'div'>(MessageComponent);
